/**
 * Team Entitlement Types
 *
 * CANONICAL SOURCE OF TRUTH for team plan/subscription state.
 * This replaces all legacy fields:
 * - plan, planActivatedAt, planMeta
 * - subscriptionPlan, subscriptionState, subscriptionStartedAt
 * - trialStartedAt, trialStatus, trialEndsAt, trialExtensionLog
 *
 * The `entitlement` object on the team document is the ONLY field
 * that should be read/written for plan-related logic.
 */

import type { Timestamp } from 'firebase/firestore';

// ============================================================================
// Plan Tiers
// ============================================================================

/**
 * Plan tier — coarse gate used for all paid-only functionality.
 * - free: Limited features and row counts (15 per collection)
 * - paid: Full access, no limits
 *
 * Future paid variants MUST be expressed via PlanId, not new tiers.
 */
export type PlanTier = 'free' | 'paid';

/**
 * Plan variant within a tier.
 * - free: Free plan
 * - paid_default: Default paid plan
 * Future: 'paid_starter', 'paid_pro', 'paid_enterprise', bundles, etc.
 */
export type PlanId = 'free' | 'paid_default';

/**
 * Access status — determines whether paid features are currently accessible.
 * - free: Team is on free plan
 * - active: Paid plan, full access
 * - restricted: Paid plan but access blocked (payment failed, deactivated, etc.)
 */
export type AccessStatus = 'free' | 'active' | 'restricted';

/**
 * @deprecated Use PlanTier ('free' | 'paid') instead.
 * Kept for legacy Firestore document compatibility and migration.
 * The resolver maps these to AccessStatus automatically.
 */
export type LegacyPlanTier = 'free' | 'starter' | 'pro' | 'enterprise';

/**
 * @deprecated Use AccessStatus instead.
 * Kept for legacy Firestore document compatibility and migration.
 * The resolver maps these to AccessStatus automatically.
 */
export type EntitlementState = 'free' | 'trialing' | 'active' | 'canceled' | 'past_due' | 'expired';

/**
 * Entitlement state - determines current access status
 * - free: On free plan (no trial, no subscription)
 * - trialing: In trial period (has access to paid features)
 * - active: Active paid subscription
 * - canceled: Subscription canceled but still has access until period end
 * - past_due: Payment failed, grace period
 * - expired: Trial or subscription ended, no access
 */
export type EntitlementState = 'free' | 'trialing' | 'active' | 'canceled' | 'past_due' | 'expired';

/**
 * Source of the entitlement assignment
 */
export type EntitlementSource =
  | 'signup' // Set during registration
  | 'trial' // Started a trial
  | 'manual' // Admin manually set
  | 'stripe' // Stripe webhook
  | 'migration' // Migrated from legacy fields
  | 'upgrade' // User upgraded
  | 'downgrade' // User downgraded
  | 'settings'; // Changed via settings UI

/**
 * Cancellation status for subscription cancellation with notice period
 * - none: No cancellation requested
 * - scheduled: Cancellation requested, waiting for effective date
 * - effective: Cancellation effective date reached, team deactivated
 */
export type CancellationStatus = 'none' | 'scheduled' | 'effective';

// ============================================================================
// Trial Information (nested, optional)
// ============================================================================

/**
 * Trial extension record
 */
export interface TrialExtension {
  type: 'feedback' | 'invite' | 'manual';
  days: number;
  createdAt: Timestamp;
  userId?: string;
  note?: string;
}

/**
 * Trial information - only present if team has/had a trial
 */
export interface TrialInfo {
  /** When the trial started */
  startedAt: Timestamp;
  /** When the trial ends/ended */
  endsAt: Timestamp;
  /** Trial extensions granted */
  extensions?: TrialExtension[];
}

// ============================================================================
// Provider Metadata (optional, future-proof)
// ============================================================================

/**
 * External provider metadata (Stripe, etc.)
 */
export interface ProviderMetadata {
  /** Provider name */
  provider: 'stripe' | 'manual' | 'legacy';
  /** External subscription ID */
  subscriptionId?: string;
  /** External customer ID */
  customerId?: string;
  /** Current billing period end */
  currentPeriodEnd?: Timestamp;
}

// ============================================================================
// Canonical Entitlement Object
// ============================================================================

/**
 * The canonical entitlement object - SINGLE SOURCE OF TRUTH
 *
 * This object lives at: teams/{teamId}.entitlement
 *
 * Example documents:
 *
 * FREE TEAM:
 * {
 *   tier: 'free',
 *   state: 'free',
 *   activatedAt: Timestamp,
 *   source: 'signup',
 *   schemaVersion: 1
 * }
 *
 * TRIALING TEAM:
 * {
 *   tier: 'starter',
 *   state: 'trialing',
 *   activatedAt: Timestamp,
 *   source: 'trial',
 *   trial: {
 *     startedAt: Timestamp,
 *     endsAt: Timestamp
 *   },
 *   schemaVersion: 1
 * }
 *
 * ACTIVE PAID TEAM:
 * {
 *   tier: 'starter',
 *   state: 'active',
 *   activatedAt: Timestamp,
 *   source: 'stripe',
 *   provider: {
 *     provider: 'stripe',
 *     subscriptionId: 'sub_xxx',
 *     customerId: 'cus_xxx',
 *     currentPeriodEnd: Timestamp
 *   },
 *   schemaVersion: 1
 * }
 */
export interface TeamEntitlement {
  /** Plan tier — 'free' | 'paid'. Coarse gate for all paid-only functionality. */
  tier: PlanTier;

  /**
   * Plan variant within the tier.
   * 'free' | 'paid_default' (future: 'paid_starter', 'paid_pro', etc.)
   */
  planId?: PlanId;

  /**
   * Access status — determines whether paid features are currently accessible.
   * 'free' | 'active' | 'restricted'
   *
   * If absent, the resolver derives it from the legacy `state` field.
   */
  accessStatus?: AccessStatus;

  /**
   * @deprecated Use `tier` + `accessStatus` instead.
   * Legacy state field kept for backward compatibility with existing Firestore docs.
   * The resolver maps this to AccessStatus automatically.
   */
  state?: EntitlementState;

  /** When this entitlement was activated/changed */
  activatedAt: Timestamp;

  /** How this entitlement was set */
  source: EntitlementSource;

  /** Trial information (optional) */
  trial?: TrialInfo;

  /** External provider metadata (optional) */
  provider?: ProviderMetadata;

  /** Admin notes (optional) */
  notes?: string;

  /** Schema version for future migrations */
  schemaVersion: number;

  // =========================================================================
  // Cancellation Fields (for subscription cancellation with notice period)
  // =========================================================================

  /** When cancellation was requested (serverTimestamp) */
  cancelRequestedAt?: Timestamp;

  /** When cancellation becomes effective (1 month after request) */
  cancelEffectiveAt?: Timestamp;

  /** Cancellation status: none | scheduled | effective */
  cancelStatus?: CancellationStatus;

  /** Source of cancellation request */
  cancelSource?: 'settings' | 'admin' | 'stripe';

  /** When team was deactivated after cancellation */
  deactivatedAt?: Timestamp;

  // =========================================================================
  // Billing Intent (for SEPA/payment setup)
  // =========================================================================

  /** Billing intent for payment setup */
  billingIntent?: BillingIntent;
}

// ============================================================================
// Current Schema Version
// ============================================================================

/**
 * Current entitlement schema version.
 * Increment when making breaking changes to the schema.
 */
export const ENTITLEMENT_SCHEMA_VERSION = 2;

// ============================================================================
// Billing Intent (for SEPA/payment setup)
// ============================================================================

/**
 * Billing intent - stores payment method info before Stripe integration
 */
export interface BillingIntent {
  /** IBAN for SEPA direct debit */
  iban: string;
  /** Account holder name */
  accountHolderName: string;
  /** SEPA mandate accepted */
  sepaAccepted: boolean;
  /** When billing intent was created */
  createdAt: Timestamp;
  /** Who created the billing intent */
  createdBy: string;
}

// ============================================================================
// Billing Event Types (for audit log)
// ============================================================================

/**
 * Billing event types for audit trail
 */
export type BillingEventType =
  | 'PLAN_UPGRADED' // Free → Pro upgrade
  | 'SUBSCRIPTION_CANCELLED_SCHEDULED' // Cancellation requested (1 month notice)
  | 'TEAM_DEACTIVATED_AFTER_CANCELLATION' // Team deactivated after effective date
  | 'BILLING_INTENT_CREATED'; // Payment method added

/**
 * Billing event document stored in /billingEvents/{id}
 */
export interface BillingEvent {
  type: BillingEventType;
  teamId: string;
  performedByUid: string;
  performedByEmail: string | null;
  createdAt: Timestamp;
  source: 'settings' | 'admin' | 'stripe' | 'scheduled';
  // Type-specific fields
  cancelRequestedAt?: Timestamp;
  cancelEffectiveAt?: Timestamp;
  reason?: string;
  previousTier?: PlanTier;
  newTier?: PlanTier;
}

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Minimal team shape for entitlement resolution
 *
 * IMPORTANT: Use the entitlement resolver functions (resolveTeamEntitlement,
 * getEffectiveTier, hasFullAccess, etc.) instead of reading fields directly.
 */
export interface TeamWithEntitlement {
  /** Canonical entitlement object - SINGLE SOURCE OF TRUTH */
  entitlement?: TeamEntitlement;

  // =========================================================================
  // LEGACY FIELDS - DO NOT READ DIRECTLY
  // These are only used by the resolver for backward compatibility.
  // Use resolveTeamEntitlement() or getEffectiveTier() instead.
  // =========================================================================

  /** @deprecated Use entitlement.tier via getEffectiveTier() instead */
  plan?: 'free' | 'paid';
  /** @deprecated Use entitlement.activatedAt instead */
  planActivatedAt?: Timestamp | null;
  /** @deprecated Use entitlement.source instead */
  planMeta?: { source?: string; notes?: string };
  /** @deprecated Use entitlement.tier via getEffectiveTier() instead */
  subscriptionPlan?: 'starter' | 'pro' | 'enterprise';
  /** @deprecated Use entitlement.state via getEntitlementState() instead */
  subscriptionState?: 'trial' | 'active' | 'cancelled' | 'past_due';
  /** @deprecated Use entitlement.activatedAt instead */
  subscriptionStartedAt?: Timestamp | null;
  /** @deprecated Use entitlement.trial.startedAt instead */
  trialStartedAt?: Timestamp | null;
  /** @deprecated Use entitlement.state instead */
  trialStatus?: 'pending' | 'active' | 'expired';
  /** @deprecated Use entitlement.trial.endsAt instead */
  trialEndsAt?: Timestamp | null;
}

/**
 * Resolved entitlement - always has all required fields
 * This is what the resolver returns after processing the entitlement object
 */
export interface ResolvedEntitlement {
  /** Coarse tier: 'free' | 'paid' */
  tier: PlanTier;
  /** Plan variant: 'free' | 'paid_default' */
  planId: PlanId;
  /** Access status: 'free' | 'active' | 'restricted' */
  accessStatus: AccessStatus;
  /**
   * @deprecated Kept for backward compatibility. Use accessStatus instead.
   * Derived from accessStatus: free→'free', active→'active', restricted→'expired'.
   */
  state: EntitlementState;
  activatedAt: Date | null;
  source: EntitlementSource;
  trial?: {
    startedAt: Date;
    endsAt: Date;
  };
  /**
   * True if team has full paid access.
   * Formula: (tier === 'paid') && (accessStatus === 'active') && (!isDeactivated)
   */
  hasFullAccess: boolean;
  /** True if tier === 'free' */
  isFree: boolean;
  /** True if tier === 'paid' */
  isPaid: boolean;
  /** True if accessStatus === 'restricted' || isDeactivated */
  isRestricted: boolean;
  /**
   * True if entitlement is missing/invalid and team needs to be recreated.
   * When true, UI should show "Create new team" message instead of crashing.
   */
  isInvalid: boolean;
  /** Error message when isInvalid is true */
  invalidReason?: string;
  /** Cancellation status */
  cancelStatus?: CancellationStatus;
  /** When cancellation becomes effective */
  cancelEffectiveAt?: Date;
  /** True if team is deactivated (after cancellation effective date) */
  isDeactivated: boolean;
}
